import { useState } from 'react';
import type { TicketStatus } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { TicketStatusFormModal } from './TicketStatusFormModal.js';
import { useTicketStatuses } from './api.js';

export function TicketStatusesPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TicketStatus | null>(null);

  const query = useTicketStatuses({ page, pageSize: DEFAULT_PAGE_SIZE, sortBy: 'sortOrder' });
  const statuses = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (status: TicketStatus): void => {
    setEditing(status);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Ticket Statuses"
        description="Configure the global, ordered list of ticket statuses."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="ticketStatuses" className="h-4 w-4" />
              New status
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Order</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Flags</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={4}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={statuses.length === 0}
              emptyTitle="No ticket statuses found"
              emptyDescription="Create your first ticket status to get started."
              emptyAction={isAdmin ? <Button onClick={openCreate}>New status</Button> : undefined}
            >
              {statuses.map((status) => (
                <tr
                  key={status.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(status)}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">{status.sortOrder}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{status.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {status.isDefault ? <Badge tone="blue">Default</Badge> : null}
                      {status.isClosed ? <Badge tone="gray">Closed</Badge> : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={status.isActive ? 'green' : 'gray'}>
                      {status.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && statuses.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <TicketStatusFormModal
        isOpen={formOpen}
        status={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
